import { AgentRole } from '@aiai/shared';
import type { AgentRole as AgentRoleT } from '@aiai/shared';

/**
 * The canonical set of agent templates — the single source of truth shared by
 * the scheduled-loop editor (`LoopEditorPage`) and the one-off "Run a task"
 * modal (`RunTaskModal`). Every template is a self-contained worker mission, so
 * each one runs cleanly both ways: hand it a schedule to make it a loop, or fire
 * it once as a single task. Keeping one list means the two flows can never drift.
 *
 * `cronExpression` is only the *default* schedule the loop editor pre-fills; the
 * one-off flow ignores it. The human-readable label is derived from the
 * expression via `cronLabel()`, so it never falls out of sync.
 */
export interface AgentTemplate {
  id: string;
  name: string;
  description: string;
  prompt: string;
  /** Which role the template runs as. All current presets are worker missions. */
  role: AgentRoleT;
  /** Default schedule for the loop editor. Ignored by the one-off task flow. */
  cronExpression: string;
}

export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    id: 'bug-sweep',
    name: 'Bug sweep',
    description:
      'Hunts the codebase for real bugs, fixes the clear ones (→ PR), and files the rest as bug issues.',
    role: AgentRole.Worker,
    cronExpression: '0 9 * * 2',
    prompt: `You are a bug hunter. Your job is to find and fix real defects — not to refactor for taste.

STEP 1 — Hunt.
Read the most important modules and look for genuine bugs: off-by-one and boundary errors, unhandled promise rejections, missing null/undefined guards, incorrect error handling, race conditions, resource leaks, and logic that contradicts its own comments or tests. Prefer a few real, reproducible bugs over a long list of nitpicks.

STEP 2 — Confirm before you touch anything.
For each candidate, convince yourself it is a real bug — trace the code path or write a failing test that reproduces it. If you cannot make it fail, it is not a bug; drop it.

STEP 3 — Fix the clear ones.
For each confirmed bug that is small and low-risk, write the fix and a test that would have caught it. Keep each fix tight and focused — do not bundle unrelated changes. Leave anything large, risky, or ambiguous for a human: file it as a "bug" issue with the reproduction and a suggested approach instead of fixing it.

STEP 4 — Verify, commit, report.
Run the full test suite and make sure it passes before committing. Commit the fixes and open a PR. File a board issue summarising what you fixed, what you left open (and why), and anything you investigated but ruled out. If you found nothing genuinely wrong, file nothing — an empty run is a good run.`,
  },
  {
    id: 'test-coverage',
    name: 'Test coverage',
    description:
      'Finds critical untested paths, adds focused tests for them, and opens a PR.',
    role: AgentRole.Worker,
    cronExpression: '0 9 * * 3',
    prompt: `You are a test engineer. Your job is to raise confidence in the code by covering the paths that matter, not by chasing a coverage percentage.

STEP 1 — Find the gaps that matter.
Identify the most important untested (or thinly tested) behaviour: core business logic, error and edge-case handling, security- or money-sensitive paths, and recently changed code. Skip trivial getters, generated code, and glue with no logic.

STEP 2 — Write focused tests.
Add tests that assert real behaviour and would fail if the logic broke — cover the happy path, the boundaries, and at least one failure mode per unit. Follow the project's existing test style, framework, and file layout. Do not change production code except to make it testable in an obviously safe way; if a real bug surfaces while writing a test, note it.

STEP 3 — Verify, commit, report.
Run the full test suite and confirm every test (new and existing) passes before committing. Commit the new tests and open a PR. File a board issue summarising what you covered, any bug the new tests exposed, and the biggest remaining coverage gaps worth a dedicated task.`,
  },
  {
    id: 'security-sweep',
    name: 'Security sweep',
    description:
      'Scans for vulnerabilities, fixes clear low-risk ones (→ PR), and files the rest as bug issues.',
    role: AgentRole.Worker,
    cronExpression: '0 9 * * 1',
    prompt: `You are a security engineer. Read the codebase carefully and look for real, exploitable weaknesses.

STEP 1 — Scan.
Look for: authentication and authorization bypasses, injection (SQL/command/template), missing input validation, exposed or hard-coded credentials, missing rate-limits on sensitive endpoints, insecure direct object references, unsafe deserialization, and secrets leaking into logs or responses. Focus on issues an attacker could actually reach.

STEP 2 — Confirm.
For each finding, establish the concrete attack path — who can trigger it and what they gain. Discard anything you cannot substantiate; a speculative finding is noise.

STEP 3 — Handle by risk.
Fix only the clear, low-risk, self-contained issues (e.g. adding a missing guard, tightening validation, removing an accidental secret) with a test where practical. Anything touching auth flows, schema/migrations, or with wide blast radius must NOT be changed automatically — file it as a "bug" issue with the attack path and a suggested remediation so a human can review.

STEP 4 — Verify, commit, report.
Run the full test suite and confirm it passes before committing. Commit any fixes and open a PR. File a board issue summarising each finding, its severity, whether you fixed it or handed it off, and why. If you found nothing exploitable, file nothing.`,
  },
  {
    id: 'docs-sync',
    name: 'Documentation sync',
    description:
      'Audits READMEs, docs, and inline comments for staleness and opens a PR with the fixes.',
    role: AgentRole.Worker,
    cronExpression: '0 9 * * 5',
    prompt: `You are the documentation agent. Your job is to keep the project's documentation accurate and up to date with the code.

STEP 1 — Audit for staleness.
Read the project's README, any docs/ directory, and inline comments for the most important modules. Compare against the actual code. Look for:
- Described commands or scripts that no longer exist or have changed flags.
- API routes, parameters, or response shapes that differ from the implementation.
- Architecture descriptions that no longer match the module structure.
- Setup steps that reference missing env vars, files, or services.
- Outdated version numbers or dependency names.

STEP 2 — Fix the discrepancies.
Update the documentation in place. Do not rewrite sections that are still accurate — make targeted corrections only. Add brief notes for new features or behaviour that lacks any documentation.

STEP 3 — Verify.
Re-read each changed section to confirm it is now accurate. If any code example in the docs can be executed, run it and confirm it passes.

STEP 4 — Commit and report.
Commit the documentation fixes and open a PR. File a board issue summarising what was stale, what was corrected, and anything left open (e.g. a section that needs a full rewrite and would benefit from a dedicated task).`,
  },
  {
    id: 'dependency-update',
    name: 'Dependency updates',
    description:
      'Updates outdated dependencies, runs the test suite, and opens a PR with the changes.',
    role: AgentRole.Worker,
    cronExpression: '0 9 * * 6',
    prompt: `You are the dependency updater. Your job is to keep this project's dependencies up to date without breaking it.

STEP 1 — Identify outdated packages.
Run the appropriate command for the detected package manager (e.g. \`npm outdated\`, \`pip list --outdated\`, \`cargo outdated\`, \`go list -m -u all\`). List every package with a newer version available.

STEP 2 — Update to the latest compatible versions.
Use the package manager's update command (e.g. \`npm update\`, \`pip install --upgrade\`, \`cargo update\`, \`go get -u ./...\`). For npm-family: also run \`npm audit fix\` to resolve any known vulnerabilities surfaced by the update. Prefer minor/patch bumps; hold major-version bumps that need code changes and note them for a human.

STEP 3 — Run the project's test suite.
Run the full test suite. If any tests break after an update, revert that specific package to the last passing version and note it in the issue body as "skipped — tests failed at <package>@<version>".

STEP 4 — Commit and report.
Commit all changes and open a PR, then file a board issue summarising: which packages were bumped (before/after versions), which were skipped and why, any major bumps deferred, and the overall test result. If nothing was outdated, file a note and do nothing.`,
  },
];
